import React, { useState } from "react"
import { Box, Button } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import InputDateField from "src/components/atom/InputDateField"
import SelectField from "src/components/atom/SelectField"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import InputTextField from "src/components/atom/InputTextField"
import dayjs, { Dayjs } from "dayjs"
import { MEMBER_STATUS, PROVINCES, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import { AddressService } from "src/openapi_address"
import { validateField, validateFields } from "src/modules/validation"
import * as yup from "yup"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import InputNameField from "src/components/atom/InputNameField"
import InputField from "src/components/atom/InputField"
import { Company, ProfileService } from "src/openapi"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { ConfirmModal } from "src/UILibrary/ConfirmModal"
import { ShowProgress } from "src/states/showProgress"

type Props = {
  item: Company
}

export const CompanyDetail = ({ item }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { companyTypes, areas, accessToken, checkLoginStatus } = useLoginContext()

  const [companyName, setCompanyName] = useState<string>(item.companyName as string)
  const [companyNameJP, setCompanyNameJP] = useState<string>(item.companyNameKana as string)
  const [firstName, setFirstName] = useState<string>(item.managerFirstName as string)
  const [lastName, setLastName] = useState<string>(item.managerLastName as string)
  const [firstNameJP, setFirstNameJP] = useState<string>(item.managerFirstNameKana as string)
  const [lastNameJP, setLastNameJP] = useState<string>(item.managerLastNameKana as string)
  const [areaID, setAreaID] = useState<number>(item.areaId as number)
  const [phoneNumber, setPhoneNumber] = useState<string>(item.phoneNumber as string)
  const [headPostCode, setHeadPostCode] = useState<string>(item.headOfficeZipCode as string)
  const [headProvince, setHeadProvince] = useState<string>(item.headOfficeProvince as string)
  const [headCity, setHeadCity] = useState<string>(item.headOfficeCity as string)
  const [headStreet, setHeadStreet] = useState<string>(item.headOfficeStreetNo as string)
  const [headBuilding, setHeadBuilding] = useState<string>(item.headOfficeBuilding as string)
  const [headSiteURL, setHeadSiteURL] = useState<string>(item.headOfficeUrl as string)
  const [headPhoneNumber, setHeadPhoneNumber] = useState<string>(
    item.headOfficePhoneNumber as string
  )
  const [officePostCode, setOfficePostCode] = useState<string>(item.officeZipCode as string)
  const [officeProvince, setOfficeProvince] = useState<string>(item.officeProvince as string)
  const [officeCity, setOfficeCity] = useState<string>(item.officeCity as string)
  const [officeStreet, setOfficeStreet] = useState<string>(item.officeStreetNo as string)
  const [officeBuilding, setOfficeBuilding] = useState<string>(item.officeBuilding as string)
  const [officeSiteURL, setOfficeSiteURL] = useState<string>(item.officeUrl as string)
  const [officePhoneNumber, setOfficePhoneNumber] = useState<string>(
    item.officePhoneNumber as string
  )
  const [companyNameValidationMessage, setCompanyNameValidationMessage] = useState<string>("")
  const [companyNameJPValidationMessage, setCompanyNameJPValidationMessage] = useState<string>("")
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [headPostCodeValidationMessage, setHeadPostCodeValidationMessage] = useState<string>("")
  const [headCityValidationMessage, setHeadCityValidationMessage] = useState<string>("")
  const [headSiteUrlValidationMessage, setHeadSiteUrlValidationMessage] = useState<string>("")
  const [headPhoneNumberValidationMessage, setHeadPhoneNumberValidationMessage] =
    useState<string>("")
  const [officePostCodeValidationMessage, setOfficePostCodeValidationMessage] = useState<string>("")
  const [officeSiteUrlValidationMessage, setOfficeSiteUrlValidationMessage] = useState<string>("")
  const [officePhoneNumberValidationMessage, setOfficePhoneNumberValidationMessage] =
    useState<string>("")

  const [startDate, setStartDate] = useState<Dayjs | null>(
    item.startupDate?.startsWith("0001-01-01") ? null : dayjs(item.startupDate as string)
  )
  const [capital, setCapital] = useState<string>(item.capitalFund?.toString() as string)
  const [anual, setAnual] = useState<string>(item.annualSales?.toString() as string)
  const [employeeCnt, setEmployCnt] = useState<string>(item.employeeCount?.toString() as string)
  const [companyType, setCompanyType] = useState<number>(item.businessTypeId as number)
  const [service, setService] = useState<string>(item.businessContent as string)
  const [capitalValidationMessage, setCapitalValidationMessage] = useState<string>("")
  const [anualValidationMessage, setAnualValidationMessage] = useState<string>("")
  const [employeeCntValidationMessage, setEmployeeCntValidationMessage] = useState<string>("")
  const [companyTypeValidationMessage, setCompanyTypeValidationMessage] = useState<string>("")

  const [lounge, setLounge] = useState<boolean>(item.isLoungeMember as boolean)
  const [accept, setAccept] = useState<string>(item.status as string)
  const [loungeValidationMessage, setLoungeValidationMessage] = useState<string>("")
  const [acceptValidationMessage, setAcceptValidationMessage] = useState<string>("")

  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  const handleHeadPostCodeInput = () => {
    if (!headPostCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(headPostCode).then((res) => {
      if (res.prefecture) {
        setHeadProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setHeadCity(city)
      }
    })
  }

  const handleOfficePostCodeInput = () => {
    if (!officePostCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(officePostCode).then((res) => {
      if (res.prefecture) {
        setOfficeProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setOfficeCity(city)
      }
    })
  }

  const validate = async () => {
    let validation = true

    try {
      await validateField(
        yup.object().shape({
          companyName: yup.string().required(t("please_input_company_name")),
        }),
        "companyName",
        companyName
      )
      validation = validation && true
      setCompanyNameValidationMessage("")
    } catch (err: any) {
      setCompanyNameValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          companyNameJP: yup
            .string()
            .required(t("please_input_company_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        }),
        "companyNameJP",
        companyNameJP
      )
      validation = validation && true
      setCompanyNameJPValidationMessage("")
    } catch (err: any) {
      setCompanyNameJPValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateFields(
        yup.object().shape({
          firstName: yup.string().required(t("please_input_first_name")),
          lastName: yup.string().required(t("please_input_last_name")),
          firstNameJP: yup
            .string()
            .required(t("please_input_first_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
          lastNameJP: yup
            .string()
            .required(t("please_input_last_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        }),
        ["firstName", "lastName", "firstNameJP", "lastNameJP"],
        [firstName, lastName, firstNameJP, lastNameJP]
      )
      validation = validation && true
      setNameValidationMessage("")
    } catch (err: any) {
      setNameValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          phoneNumber: yup
            .string()
            .required(t("please_input_phone_number"))
            .matches(
              /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
              t("please_input_correct_phone_number")
            ),
        }),
        "phoneNumber",
        phoneNumber
      )
      setPhoneNumberValidationMessage("")
    } catch (err: any) {
      setPhoneNumberValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          headPostCode: yup
            .string()
            .required(t("please_input_post_code"))
            .matches(/〒?[0-9]{3}-?[0-9]{4}/g, t("please_input_correct_post_code")),
        }),
        "headPostCode",
        headPostCode
      )
      setHeadPostCodeValidationMessage("")
    } catch (err: any) {
      setHeadPostCodeValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          headCity: yup.string().required(t("please_input_city")),
        }),
        "headCity",
        headCity
      )
      setHeadCityValidationMessage("")
    } catch (err: any) {
      setHeadCityValidationMessage(err.message)
      validation = validation && false
    }

    if (headSiteURL) {
      try {
        await validateField(
          yup.object().shape({
            headSiteURL: yup
              .string()
              .matches(
                /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
                t("please_input_correct_site_url")
              ),
          }),
          "headSiteURL",
          headSiteURL
        )
        setHeadSiteUrlValidationMessage("")
      } catch (err: any) {
        setHeadSiteUrlValidationMessage(err.message)
        validation = validation && false
      }
    }

    try {
      await validateField(
        yup.object().shape({
          headPhoneNumber: yup
            .string()
            .required(t("please_input_phone_number"))
            .matches(
              /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
              t("please_input_correct_phone_number")
            ),
        }),
        "headPhoneNumber",
        headPhoneNumber
      )
      setHeadPhoneNumberValidationMessage("")
    } catch (err: any) {
      setHeadPhoneNumberValidationMessage(err.message)
      validation = validation && false
    }

    if (officePostCode) {
      try {
        await validateField(
          yup.object().shape({
            officePostCode: yup
              .string()
              .required(t("please_input_post_code"))
              .matches(/〒?[0-9]{3}-?[0-9]{4}/g, t("please_input_correct_post_code")),
          }),
          "officePostCode",
          officePostCode
        )
        setOfficePostCodeValidationMessage("")
      } catch (err: any) {
        setOfficePostCodeValidationMessage(err.message)
        validation = validation && false
      }
    }

    if (officeSiteURL) {
      try {
        await validateField(
          yup.object().shape({
            officeSiteURL: yup
              .string()
              .matches(
                /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
                t("please_input_correct_site_url")
              ),
          }),
          "officeSiteURL",
          officeSiteURL
        )
        setOfficeSiteUrlValidationMessage("")
      } catch (err: any) {
        setOfficeSiteUrlValidationMessage(err.message)
        validation = validation && false
      }
    }

    if (officePhoneNumber) {
      try {
        await validateField(
          yup.object().shape({
            officePhoneNumber: yup
              .string()
              .required(t("please_input_phone_number"))
              .matches(
                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
                t("please_input_correct_phone_number")
              ),
          }),
          "officePhoneNumber",
          officePhoneNumber
        )
        setOfficePhoneNumberValidationMessage("")
      } catch (err: any) {
        setOfficePhoneNumberValidationMessage(err.message)
        validation = validation && false
      }
    }

    if (capital) {
      try {
        await validateField(
          yup.object().shape({
            capital: yup.string().matches(/[0-9.]/, t("please_input_number")),
          }),
          "capital",
          capital
        )
        setCapitalValidationMessage("")
      } catch (err: any) {
        setCapitalValidationMessage(err.message)
        validation = validation && false
      }
    }

    if (anual) {
      try {
        await validateField(
          yup.object().shape({
            anual: yup.string().matches(/[0-9.]/, t("please_input_number")),
          }),
          "anual",
          anual
        )
        setAnualValidationMessage("")
      } catch (err: any) {
        setAnualValidationMessage(err.message)
        validation = validation && false
      }
    }

    if (employeeCnt) {
      try {
        await validateField(
          yup.object().shape({
            employeeCnt: yup.string().matches(/[0-9.]/, t("please_input_number")),
          }),
          "employeeCnt",
          employeeCnt
        )
        setEmployeeCntValidationMessage("")
      } catch (err: any) {
        setEmployeeCntValidationMessage(err.message)
        validation = validation && false
      }
    }

    try {
      await validateField(
        yup.object().shape({
          companyType: yup.number().required(t("please_select_company_type_pr")),
        }),
        "companyType",
        companyType
      )
      setCompanyTypeValidationMessage("")
    } catch (err: any) {
      setCompanyTypeValidationMessage(err.message)
      validation = validation && false
    }

    if (lounge === undefined) {
      setLoungeValidationMessage(t("please_select_lounge"))
      validation = validation && false
    } else {
      setLoungeValidationMessage("")
    }

    if (accept === undefined) {
      setAcceptValidationMessage(t("please_select_status"))
      validation = validation && false
    } else {
      setAcceptValidationMessage("")
    }

    return validation
  }

  const handleUpdate = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    ProfileService.updateCompany(accessToken, item.userId as string, {
      companyName: companyName,
      companyNameKana: companyNameJP,
      managerFirstName: firstName,
      managerLastName: lastName,
      managerFirstNameKana: firstNameJP,
      managerLastNameKana: lastNameJP,
      phoneNumber: phoneNumber,
      areaId: areaID,
      headOfficeZipCode: headPostCode,
      headOfficeProvince: headProvince,
      headOfficeCity: headCity,
      headOfficeStreetNo: headStreet,
      headOfficeBuilding: headBuilding,
      headOfficePhoneNumber: headPhoneNumber,
      headOfficeUrl: headSiteURL,
      officeZipCode: officePostCode,
      officeProvince: officeProvince,
      officeCity: officeCity,
      officeStreetNo: officeStreet,
      officeBuilding: officeBuilding,
      officeUrl: officeSiteURL,
      officePhoneNumber: officePhoneNumber,
      startupDate: startDate?.isValid() ? startDate.format("YYYY-MM-DD") : undefined,
      capitalFund: parseFloat(capital),
      annualSales: parseFloat(anual),
      employeeCount: parseFloat(employeeCnt),
      businessContent: service,
      businessTypeId: companyType as number,
      isLoungeMember: lounge,
      status: accept,
      comment: "COMPANY_UPDATE",
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_update"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleRemove = () => {
    setShowConfirm(true)
  }

  const handleRemoveConfirm = () => {
    setShowProgress(true)
    ProfileService.deleteCompany(accessToken, item.userId as string)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_remove"), type: SNACKBAR_TYPES.SUCCESS })
          location.reload()
        } else {
          setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Box sx={{ py: "40px", px: "20px" }}>
      <InputTextField
        name={t("company_name")}
        required
        sx={{ width: "100%" }}
        text={companyName}
        handleText={setCompanyName}
        validationMessage={companyNameValidationMessage}
        maxLength={100}
      />
      <InputTextField
        name={t("company_name_jp")}
        required
        sx={{ width: "100%", mt: "10px" }}
        text={companyNameJP}
        handleText={setCompanyNameJP}
        validationMessage={companyNameJPValidationMessage}
        maxLength={100}
      />
      <InputNameField
        name={t("charge_name")}
        required
        sx={{ mt: "30px" }}
        firstName={firstName}
        lastName={lastName}
        firstNameJP={firstNameJP}
        lastNameJP={lastNameJP}
        handleFirstName={setFirstName}
        handleLastName={setLastName}
        handleFirstNameJP={setFirstNameJP}
        handleLastNameJP={setLastNameJP}
        validationMessage={nameValidationMessage}
      ></InputNameField>
      <SelectField
        name={t("address")}
        required
        sx={{ mt: "30px", mb: "40px", width: "200px" }}
        selected={areaID}
        handleSelected={setAreaID}
        datas={areas}
      ></SelectField>
      <InputTextField
        name={t("phone_number")}
        required
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={phoneNumber}
        handleText={setPhoneNumber}
        validationMessage={phoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <InputField name={t("company_address")} required sx={{ mt: "80px" }} />
      <InputTextField
        name={t("post_code")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={headPostCode}
        handleText={setHeadPostCode}
        validationMessage={headPostCodeValidationMessage}
        handleFocusOut={handleHeadPostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        sx={{ mt: "30px", width: "200px" }}
        selected={headProvince}
        handleSelected={setHeadProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        sx={{ mt: "30px" }}
        text={headCity}
        handleText={setHeadCity}
        validationMessage={headCityValidationMessage}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={headStreet}
        handleText={setHeadStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={headBuilding}
        handleText={setHeadBuilding}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("site_url")}
        sx={{ mt: "30px" }}
        text={headSiteURL}
        handleText={setHeadSiteURL}
        validationMessage={headSiteUrlValidationMessage}
        maxLength={200}
      ></InputTextField>
      <InputTextField
        name={t("phone_number")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={headPhoneNumber}
        handleText={setHeadPhoneNumber}
        validationMessage={headPhoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <InputField name={t("service_input")} sx={{ mt: "80px" }} />
      <InputTextField
        name={t("post_code")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={officePostCode}
        handleText={setOfficePostCode}
        validationMessage={officePostCodeValidationMessage}
        handleFocusOut={handleOfficePostCodeInput}
        maxLength={10}
      ></InputTextField>
      <SelectField
        name={t("province")}
        sx={{ mt: "30px", width: "200px" }}
        selected={officeProvince}
        handleSelected={setOfficeProvince}
        datas={PROVINCES}
      ></SelectField>
      <InputTextField
        name={t("city")}
        sx={{ mt: "30px" }}
        text={officeCity}
        handleText={setOfficeCity}
        maxLength={50}
      ></InputTextField>
      <InputTextField
        name={t("street")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={officeStreet}
        handleText={setOfficeStreet}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("building")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
        text={officeBuilding}
        handleText={setOfficeBuilding}
        maxLength={100}
      ></InputTextField>
      <InputTextField
        name={t("site_url")}
        sx={{ mt: "30px" }}
        text={officeSiteURL}
        handleText={setOfficeSiteURL}
        validationMessage={officeSiteUrlValidationMessage}
        maxLength={200}
      ></InputTextField>
      <InputTextField
        name={t("phone_number")}
        sx={{ mt: "30px" }}
        placeholder={t("input_normal_string")}
        text={officePhoneNumber}
        handleText={setOfficePhoneNumber}
        validationMessage={officePhoneNumberValidationMessage}
        maxLength={15}
      ></InputTextField>
      <InputDateField name={t("company_start")} date={startDate} handleDate={setStartDate} />
      <InputTextField
        name={t("capital")}
        adornment={t("10000_jpy")}
        sx={{ mt: "30px" }}
        text={capital}
        handleText={setCapital}
        validationMessage={capitalValidationMessage}
        maxLength={10}
      />
      <InputTextField
        name={t("anual")}
        adornment={t("10000_jpy")}
        sx={{ mt: "30px" }}
        text={anual}
        handleText={setAnual}
        validationMessage={anualValidationMessage}
        maxLength={10}
      />
      <InputTextField
        name={t("number_of_employees")}
        adornment={t("unit_man")}
        sx={{ mt: "30px" }}
        text={employeeCnt}
        handleText={setEmployCnt}
        validationMessage={employeeCntValidationMessage}
        maxLength={10}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("company_type_pr")}
        datas={companyTypes}
        selected={companyType}
        handleSelected={setCompanyType}
        sx={{ mt: "80px" }}
        validationMessage={companyTypeValidationMessage}
      />
      <InputTextField
        name={t("company_services")}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "690px" }}
        placeholder={t("company_services_placeholder")}
        multiline
        text={service}
        handleText={setService}
        maxLength={400}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("is_lounge")}
        datas={[
          {
            label: t("lounge"),
            value: true,
          },
          {
            label: t("not_lounge"),
            value: false,
          },
        ]}
        selected={lounge}
        handleSelected={setLounge}
        sx={{ mt: "30px" }}
        validationMessage={loungeValidationMessage}
      />
      <CheckBoxGroupField
        required
        radio
        name={t("member_status")}
        datas={[
          { label: t("requested"), value: MEMBER_STATUS.REQUEST },
          { label: t("accepted"), value: MEMBER_STATUS.ACCEPTED },
        ]}
        selected={accept}
        handleSelected={setAccept}
        sx={{ mt: "30px" }}
        validationMessage={acceptValidationMessage}
      />
      <Box display="flex" sx={{ mt: "30px" }}>
        <OutlinedButton
          caption={t("delete")}
          fontColor="#FC3317"
          fontWeight="700"
          fontSize={16}
          sx={{
            borderColor: "#FC3317",
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: "auto",
            mr: "40px",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
          }}
          onClick={() => handleRemove()}
        />
        <Button
          variant="contained"
          sx={{
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: 0,
            mr: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleUpdate()}
        >
          {t("update")}
        </Button>
      </Box>
      <ConfirmModal
        open={showConfirm}
        handleOpen={setShowConfirm}
        text={t("do_you_want_to_remove")}
        confirmText={t("remove")}
        cancelText={t("cancel")}
        handleConfirm={handleRemoveConfirm}
        confirmStyle={{ color: "#FC3317", borderColor: "#FC3317" }}
      />
    </Box>
  )
}
